import { useEffect, useMemo, useRef, useState } from "react"
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom"
import { Button } from "@/modules/shared/ui/components/custom/button"
import { apiClient } from "@/lib/api-client"

import type { BillingPlanDto } from "@/modules/subscriptions/dtos/billing-plan.dto"

type BillingInterval = "Monthly" | "Annual"

type PayFastRedirectResult = {
  redirectUrl: string
  formData: Record<string, string>
  subscriptionId: string
  signature: string
}

type CreateSubscriptionResponseDto = {
  subscription: unknown
  paymentRedirect: PayFastRedirectResult | null
  trialActivated: boolean
}

type PayfastNavState = {
  plan?: BillingPlanDto
  billingInterval?: BillingInterval
  billingCycle?: BillingInterval
}

function isBillingInterval(value: string | null): value is BillingInterval {
  return value === "Monthly" || value === "Annual"
}

function isRedirectResult(value: unknown): value is PayFastRedirectResult {
  if (!value || typeof value !== "object") return false
  const v = value as Record<string, unknown>

  return (
    typeof v.redirectUrl === "string" &&
    v.formData != null &&
    typeof v.formData === "object" &&
    typeof v.subscriptionId === "string"
  )
}

export default function PayfastPage() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navState = (location.state ?? {}) as PayfastNavState

  const paymentStatus = searchParams.get("status")
  const planIdFromQuery = searchParams.get("planId")

  const intervalFromQueryRaw =
    searchParams.get("billingCycle") ?? searchParams.get("interval")

  const billingIntervalFromQuery = isBillingInterval(intervalFromQueryRaw)
    ? intervalFromQueryRaw
    : null

  const billingInterval: BillingInterval =
    navState.billingInterval ??
    navState.billingCycle ??
    billingIntervalFromQuery ??
    "Monthly"

  const formRef = useRef<HTMLFormElement | null>(null)

  const [redirectData, setRedirectData] =
    useState<PayFastRedirectResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [plan, setPlan] = useState<BillingPlanDto | null>(navState.plan ?? null)
  const [planError, setPlanError] = useState<string | null>(null)
  const [subscribeError, setSubscribeError] = useState<string | null>(null)

  const recurringAmount = useMemo(() => {
    if (!plan) return null
    return billingInterval === "Monthly"
      ? plan.monthlyPriceIncVat
      : plan.annualPriceIncVat
  }, [plan, billingInterval])

  useEffect(() => {
    if (plan) return
    if (!planIdFromQuery) return

    const loadPlan = async () => {
      try {
        setPlanError(null)

        try {
          const { data } = await apiClient.get<BillingPlanDto>(
            `/billingplan/${encodeURIComponent(planIdFromQuery)}`,
          )
          setPlan(data)
          return
        } catch {
          const { data: plans } = await apiClient.get<BillingPlanDto[]>(
            "/billingplan/active",
          )

          const selected =
            plans.find((p) => String(p.id) === String(planIdFromQuery)) ?? null

          if (!selected) {
            setPlanError("Selected plan was not found.")
            return
          }

          setPlan(selected)
        }
      } catch (e) {
        console.error(e)
        setPlanError("Failed to load plan details")
      }
    }

    loadPlan()
  }, [plan, planIdFromQuery])

  console.log("PayFast form action:", formRef.current?.action)
  useEffect(() => {
    if (redirectData && formRef.current) formRef.current.submit()
  }, [redirectData])

  if (paymentStatus === "success") return <Navigate to="/" replace />

  if (!planIdFromQuery && !plan) {
    return (
      <div className="p-8 max-w-xl mx-auto space-y-4">
        <h1 className="text-2xl font-bold mb-2">No plan selected</h1>
        <p className="text-muted-foreground">
          Choose a billing plan before continuing.
        </p>
        <Link to="/billing-plans">
          <Button>Go to Billing Plans</Button>
        </Link>
      </div>
    )
  }

  const handleSubscribe = async () => {
    if (planError) {
      setSubscribeError(planError)
      return
    }

    const planId = String(planIdFromQuery ?? plan?.id ?? "")
    if (!planId) {
      setSubscribeError("No plan selected. Please go back and choose a plan.")
      return
    }

    try {
      setIsLoading(true)
      setSubscribeError(null)

      const { data } = await apiClient.post<CreateSubscriptionResponseDto>(
        "/subscription/create",
        {
          planId,
          billingInterval,
        },
      )

      if (!isRedirectResult(data.paymentRedirect)) {
        if (data.trialActivated) {
          // Trial started — no PayFast redirect needed
          window.location.href = "/dashboard"
          return
        }
        setSubscribeError(
          "Subscription created, but PayFast redirect format is invalid. Please contact support.",
        )
        return
      }

      setRedirectData(data.paymentRedirect)
    } catch (err) {
      console.error("Failed to create subscription", err)

      const axiosError = err as {
        response?: {
          data?: {
            message?: string
            error?: string
          }
        }
        message?: string
      }

      const errorMessage =
        axiosError?.response?.data?.message ??
        axiosError?.response?.data?.error ??
        axiosError?.message ??
        "Please try again."

      setSubscribeError(`Failed to create subscription. ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="p-8 max-w-xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold mb-2">Confirm subscription</h1>
      <p className="text-muted-foreground">
        We'll create your subscription and redirect you to PayFast to complete
        payment.
      </p>

      <div className="border rounded-lg p-4 space-y-2 bg-card">
        <div className="flex justify-between">
          <span>Plan</span>
          <span className="font-medium">
            {plan?.name ?? "Selected Plan"} ({billingInterval})
          </span>
        </div>

        {recurringAmount != null ? (
          <div className="flex justify-between">
            <span>Recurring amount</span>
            <span className="font-semibold">
              R {recurringAmount.toFixed(2)} /{" "}
              {billingInterval === "Monthly" ? "month" : "year"} (incl. VAT)
            </span>
          </div>
        ) : null}

        {planError ? (
          <p className="text-xs text-red-500 mt-2">{planError}</p>
        ) : null}
        {subscribeError ? (
          <p className="text-xs text-red-500 mt-2">{subscribeError}</p>
        ) : null}
      </div>

      {redirectData ? (
        <form
          ref={formRef}
          method="POST"
          action={redirectData.redirectUrl}
          className="hidden"
        >
          {Object.entries(redirectData.formData).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
      ) : null}

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleSubscribe}
          disabled={isLoading || !!planError}
          className="opacity-100! disabled:opacity-50! disabled:visible! disabled:block!"
        >
          {isLoading ? "Redirecting to PayFast..." : "Continue to PayFast"}
        </Button>

        <Link to="/billing-plans">
          <Button type="button" variant="outline">
            Change plan
          </Button>
        </Link>
      </div>
    </div>
  )
}
